// Packages and components
import React, { useState } from 'react';
import { Calendar } from 'grommet-icons';
import { FormField, DateInput } from 'grommet';
import SHDCalendarHeader from '@ui/Calendar/SHDCalendarHeader';

// Helpers and utilities
import { generateRandomString } from '@theme/utils/data';

// Types
import type { EventTargetValue } from '@theme/theme.d';
import type { SHDCalendarHeaderProps } from '@ui/Calendar/SHDCalendar.d';
import type { SHDInputDateProps } from './SHDInputDate.d';

const id = `input-date__${generateRandomString(6)}`;

const SHDInputDate: React.FC<SHDInputDateProps> = ({
  className = 'input-date',
  error = null,
  label = 'Date',
  showIcon = true,
  size = 'small',
  value = null,
  onChange,
}) => {
  // States
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string | string[] | null>(value);

  // Constants
  const calendarProps = {
    a11yTitle: 'Calendar for date input',
    animate: false,
    daysOfWeek: false,
    fill: false,
    showAdjacentDays: true,
    size: size,
    header: ({
      date,
      locale,
      onPreviousMonth,
      onNextMonth,
      previousInBound,
      nextInBound,
    }: SHDCalendarHeaderProps) => {
      return (
        <SHDCalendarHeader
          className={`${className}__calendar`}
          date={date}
          locale={locale}
          onPreviousMonth={onPreviousMonth}
          onNextMonth={onNextMonth}
          previousInBound={previousInBound}
          nextInBound={nextInBound}
        />
      );
    },
  };

  const dropProps = {
    background: 'white',
    stretch: false,
    round: 'xxsmall',
  };

  // Methods
  const getCalendarButton = (target: EventTarget) => {
    const input = target as HTMLInputElement;
    return input.parentElement?.nextSibling as HTMLButtonElement;
  };

  // Handlers
  const handleFocusIn = (target: EventTarget) => {
    const calendarButton = getCalendarButton(target);
    if (calendarButton && !isFocused) {
      calendarButton.click();
    }
  };

  const handleOnChange = (event: EventTargetValue) => {
    setIsFocused(true);
    const dateOnly =
      typeof event.value === 'string' && event.value.includes('T')
        ? event.value.split('T')[0]
        : event.value;

    setInputValue(dateOnly);
    onChange(dateOnly);
  };

  return (
    <div className={`${className} full-width`}>
      <FormField
        className={`${className}__label`}
        error={error?.message}
        htmlFor={id}
        label={label}
      >
        <DateInput
          calendarProps={calendarProps}
          className={`${className}__input`}
          dropProps={dropProps}
          format={inputValue ? 'mm/dd/yyyy' : 'Select a date'}
          icon={showIcon ? <Calendar /> : <Calendar color="transparent" />}
          id={id}
          inline={false}
          name={id}
          onBlur={() => setIsFocused(false)}
          onChange={(event) => handleOnChange(event)}
          onFocus={(event) => handleFocusIn(event.target)}
          value={inputValue}
        />
      </FormField>
    </div>
  );
};

export default SHDInputDate;
